"use client";

import { useState } from "react";
import { Minus, Plus, TriangleAlert } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tocItems = [{
  id: "m-01",
  index: "01",
  label: "SUPERVISION PROTOCOLS"
}, {
  id: "m-02",
  index: "02",
  label: "EXCHANGE PROCEDURES"
}, {
  id: "m-03",
  index: "03",
  label: "REPORTING STANDARDS"
}, {
  id: "m-04",
  index: "04",
  label: "SAFETY & BOUNDARIES"
}, {
  id: "m-05",
  index: "05",
  label: "INTAKE REQUIREMENTS"
}, {
  id: "m-06",
  index: "06",
  label: "CANCELLATION POLICY"
}, {
  id: "m-07",
  index: "07",
  label: "THERAPEUTIC SUPERVISION POLICY"
}];
const manualEntries = [{
  id: "m-01",
  index: "M-01",
  title: "SUPERVISION PROTOCOLS",
  instructions: ["All supervised parenting time sessions are conducted in a neutral, child-centered environment.", "Our staff maintains constant visual and auditory contact with the child and parent at all times.", "We enforce firm boundaries regarding discussion topics, ensuring no conflict or litigation is discussed in the child's presence.", "Staff will intervene immediately if any behavior is deemed inappropriate, unsafe, or contrary to court orders.", "Documentation of the session is objective, neutral, and focused on the child's well-being and interactions."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervision_protocol_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervision_protocol_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervision_protocol_diagram.png-wvc-sizes",
    alt: "Diagram showing neutral supervision environment layout",
    tag: "[FIG. 01 / NEUTRAL SPACE]"
  },
  caution: "Any attempt to bypass supervision protocols will result in immediate session termination."
}, {
  id: "m-02",
  index: "M-02",
  title: "EXCHANGE PROCEDURES",
  instructions: ["Monitored exchanges are designed to minimize contact between parents to ensure safety and reduce conflict.", "Parents must arrive at the designated time; late arrivals may result in the cancellation of the exchange.", "Children are transferred directly from one parent to our staff, then to the other parent, ensuring no direct interaction.", "We require all necessary items (diapers, medication, etc.) to be handed over to staff during the exchange.", "Exchanges are conducted in a calm, structured manner to prevent distress to the child."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/exchange_procedure_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/exchange_procedure_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/exchange_procedure_diagram.png-wvc-sizes",
    alt: "Diagram showing structured exchange flow",
    tag: "[FIG. 02 / FLOW CONTROL]"
  },
  caution: "Direct communication between parents during the exchange is strictly prohibited."
}, {
  id: "m-03",
  index: "M-03",
  title: "REPORTING STANDARDS",
  instructions: ["Observation reports are provided to the court and relevant legal parties as requested.", "Reports are strictly factual, neutral, and trauma-informed, focusing on observable behaviors.", "We do not provide opinions or recommendations regarding custody or parenting time arrangements.", "Reports are finalized and distributed within the agreed-upon timeframe following the session.", "All records are maintained with the highest level of confidentiality and security."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/reporting_standards_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/reporting_standards_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/reporting_standards_diagram.png-wvc-sizes",
    alt: "Diagram of reporting documentation process",
    tag: "[FIG. 03 / FACTUAL LOG]"
  },
  caution: "Requests for report modifications or subjective input will be declined to maintain neutrality."
}, {
  id: "m-04",
  index: "M-04",
  title: "SAFETY & BOUNDARIES",
  instructions: ["We maintain a zero-tolerance policy for threats, violence, or harassment toward staff or children.", "All participants must adhere to the established safety guidelines and behavioral expectations.", "Staff are trained to recognize and respond to signs of distress or trauma in children.", "We reserve the right to terminate any session if safety is compromised or boundaries are violated.", "Our agency operates with firm, clear boundaries to ensure a predictable and safe experience for all."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/safety_boundaries_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/safety_boundaries_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/safety_boundaries_diagram.png-wvc-sizes",
    alt: "Diagram of safety and boundary framework",
    tag: "[FIG. 04 / SAFETY FIRST]"
  },
  caution: "Any safety violation will be documented and reported to the appropriate authorities."
}, {
  id: "m-05",
  index: "M-05",
  title: "INTAKE REQUIREMENTS",
  instructions: ["All families must complete the intake process before services can be scheduled.", "Required documentation includes court orders, identification, and signed service agreements.", "An initial consultation is conducted to understand the family's needs and ensure our services are a fit.", "We require a clear understanding of the court-ordered parameters for supervision or exchange.", "Intake is a collaborative process designed to set clear expectations for all parties involved."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_process_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_process_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_process_diagram.png-wvc-sizes",
    alt: "Diagram of the intake and onboarding process",
    tag: "[FIG. 05 / ONBOARDING]"
  },
  caution: "Incomplete documentation will delay the start of services."
}, {
  id: "m-06",
  index: "M-06",
  title: "CANCELLATION POLICY",
  instructions: ["Cancellations must be made within the timeframe specified in the service agreement.", "Late cancellations or no-shows may be subject to fees as outlined in our policy.", "We understand that emergencies occur and ask that you notify us as soon as possible.", "Consistent cancellations may lead to a review of the service agreement.", "Our goal is to provide consistent, reliable support for your family's needs."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/cancellation_policy_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/cancellation_policy_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/cancellation_policy_diagram.png-wvc-sizes",
    alt: "Diagram of cancellation and scheduling policy",
    tag: "[FIG. 06 / SCHEDULING]"
  },
  caution: "Repeated cancellations disrupt the child's routine and may impact service continuity."
}, {
  id: "m-07",
  index: "M-07",
  title: "THERAPEUTIC SUPERVISION POLICY",
  instructions: ["Monitors are provided under clinician oversight.", "Clinicians review the case, guide the process, and support treatment goals.", "Monitors complete visits per therapeutic standards.", "Clinician direct presence is not required per visit since supervised visitation is not psychotherapy.", "This approach reduces costs while preserving quality, structure, and therapeutic focus."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/therapeutic_supervision_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/therapeutic_supervision_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/therapeutic_supervision_diagram.png-wvc-sizes",
    alt: "Diagram of therapeutic supervision oversight model",
    tag: "[FIG. 07 / CLINICAL OVERSIGHT]"
  },
  caution: "Therapeutic supervision is distinct from active psychotherapy sessions."
}];
export default function Policies() {
  const [activeId, setActiveId] = useState<string>("");
  const {
    ref: bannerRef,
    isVisible: bannerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const handleTocClick = (id: string) => {
    const el = document.getElementById(`accordion-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <section data-section-id="1770" id="detailedpoliciesaccordion" className="bg-[#E7D8C7] py-28 md:py-40 text-[#2F3631]">
      <div className="mx-auto max-w-[1360px] px-6 md:px-8 lg:px-16">
        <div ref={bannerRef} className={`transition-all duration-[600ms] cubic-bezier(0.16, 1, 0.3, 1) ${bannerVisible ? "translate-y-0 opacity-100" : "translate-y-[20px] opacity-0"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-block size-2.5 bg-[#A3B18A] rounded-lg" aria-hidden="true" />
                <span className="font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-[#2F3631]">
                  INDEX: 01.0 POLICIES
                </span>
              </div>

              <h1 className="font-serif font-normal leading-[1.1] tracking-tight text-[#2F3631]" style={{
              fontSize: "clamp(2.5rem, 6vw, 4.5rem)"
            }}>
                Agency Framework & Safety
              </h1>
              
              <div className="mt-8">
                <span className="inline-block font-mono text-xs font-semibold uppercase tracking-[0.12em] text-[#2F3631] border border-[#2F3631] px-3 py-1.5 rounded-lg">
                  [REV. 2025.01 / POLICY GUIDE]
                </span>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="relative aspect-[4/3] overflow-hidden rounded-lg shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/clinical_office_setting.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/clinical_office_setting.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/clinical_office_setting.png-wvc-sizes" alt="Calm clinical office setting with natural light" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 mb-16 h-6 w-full" style={{
        backgroundImage: "repeating-linear-gradient(45deg, #5E6A52 0, #5E6A52 2px, transparent 2px, transparent 8px), repeating-linear-gradient(-45deg, #5E6A52 0, #5E6A52 2px, transparent 2px, transparent 8px)",
        backgroundColor: "transparent",
        opacity: 0.4
      }} aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:hidden -mx-6 px-6 overflow-x-auto pb-4">
              <div className="flex gap-2 min-w-max">
                {tocItems.map((item, i) => <button key={item.id} data-index={i} onClick={() => handleTocClick(item.id)} className={`font-mono text-xs font-semibold uppercase tracking-[0.12em] px-3 py-2 border rounded-lg whitespace-nowrap transition-colors ${activeId === item.id ? "border-[#5E6A52] bg-[#5E6A52] text-white" : "border-[#2F3631]/40 text-[#2F3631] hover:border-[#2F3631]"}`}>
                    {item.index} / {item.label}
                  </button>)}
              </div>
            </div>

            <div className="hidden lg:block sticky top-24">
              <div className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#2F3631]/60 mb-6 flex items-center gap-2">
                <span className="inline-block size-2 bg-[#A3B18A] rounded-lg" aria-hidden="true" />
                Contents
              </div>
              <ul className="space-y-1">
                {tocItems.map((item, i) => {
                const isActive = activeId === item.id;
                return <li data-index={i} key={item.id}>
                      <button onClick={() => handleTocClick(item.id)} className={`group w-full text-left flex items-baseline gap-3 py-3 pr-2 border-l-2 transition-all duration-200 ${isActive ? "border-[#5E6A52] pl-4" : "border-transparent pl-2 hover:border-[#2F3631]/40 hover:pl-3"}`}>
                        <span className="font-mono text-xs font-semibold tracking-[0.12em] text-[#2F3631]/60 shrink-0">
                          {item.index} /
                        </span>
                        <span className={`font-sans font-bold uppercase text-sm tracking-tight transition-colors ${isActive ? "text-[#2F3631]" : "text-[#2F3631]/80 group-hover:text-[#2F3631]"}`}>
                          {item.label}
                        </span>
                      </button>
                    </li>;
              })}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <Accordion type="single" collapsible className="w-full" onValueChange={val => setActiveId(val || "")}>
              {manualEntries.map((entry, i) => <AccordionItem key={entry.id} value={entry.id} id={`accordion-${entry.id}`} data-index={i} className="border-t border-b-0 border-[#2F3631] last:border-b last:border-[#2F3631] data-[state=open]:border-l-4 data-[state=open]:border-l-[#5E6A52] transition-all">
                  <AccordionTrigger className="hover:no-underline py-8 md:py-9 [&>svg]:hidden group rounded-lg">
                    <div className="grid grid-cols-[64px_1fr_40px] md:grid-cols-[96px_1fr_40px] gap-4 md:gap-6 items-center w-full">
                      <span className="font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.12em] text-[#2F3631]/60">
                        [{entry.index}]
                      </span>
                      <span className="font-sans font-bold uppercase tracking-tight leading-none text-left text-[#2F3631]" style={{
                    fontSize: "clamp(1.25rem, 2.1vw, 2.125rem)"
                  }}>
                        {entry.title}
                      </span>
                      <span className="size-10 flex items-center justify-center bg-[#5E6A52] text-white justify-self-end shrink-0 rounded-lg shadow-md">
                        <Plus className="size-5 group-data-[state=open]:hidden" strokeWidth={2.5} />
                        <Minus className="size-5 hidden group-data-[state=open]:block" strokeWidth={2.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pt-2">
                    <div className="md:ml-24 bg-[#D9C2A6] p-6 md:p-8 border border-[#2F3631]/15 rounded-lg shadow-md">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <div className="font-mono text-xs font-semibold uppercase tracking-[0.12em] text-[#2F3631]/60 mb-4 flex items-center gap-2">
                            <span className="inline-block size-1.5 bg-[#A3B18A] rounded-lg" aria-hidden="true" />
                            Procedure
                          </div>
                          <ol className="space-y-3 font-sans text-base leading-relaxed text-[#2F3631]">
                            {entry.instructions.map((step, j) => <li key={j} data-index={j} className="grid grid-cols-[28px_1fr] gap-3">
                                <span className="font-mono text-xs font-semibold tracking-[0.1em] text-[#2F3631]/60 pt-1">
                                  {String(j + 1).padStart(2, "0")}
                                </span>
                                <span>{step}</span>
                              </li>)}
                          </ol>
                          {entry.caution && <div className="mt-6 pt-4 border-t border-[#2F3631]/15">
                              <p className="font-mono text-xs font-semibold uppercase tracking-[0.1em] flex items-start gap-2 text-[#2F3631]">
                                <TriangleAlert className="size-4 shrink-0 mt-0.5" style={{
                            color: "#5E6A52"
                          }} strokeWidth={2.5} />
                                <span>Caution — {entry.caution}</span>
                              </p>
                            </div>}
                        </div>

                        <div className="relative">
                          <div className="relative rounded-lg overflow-hidden">
                            <span className="absolute top-2 left-2 size-1.5 bg-[#2F3631] z-10 rounded-lg" aria-hidden="true" />
                            <span className="absolute top-2 right-2 size-1.5 bg-[#2F3631] z-10 rounded-lg" aria-hidden="true" />
                            <span className="absolute bottom-2 left-2 size-1.5 bg-[#2F3631] z-10 rounded-lg" aria-hidden="true" />
                            <span className="absolute bottom-2 right-2 size-1.5 bg-[#2F3631] z-10 rounded-lg" aria-hidden="true" />
                            <img src={entry.figure.src} data-wvc-srcset={entry.figure.srcset} data-wvc-sizes={entry.figure.sizes} alt={entry.figure.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover border border-[#2F3631]/30 rounded-lg" />
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-[#2F3631]/60">
                              {entry.figure.tag}
                            </span>
                            <span className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-[#2F3631]/60">
                              REF. {entry.index}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}